import {
  concat,
  curry,
  filter,
  find,
  flatten,
  intersection,
  isNil,
  map,
  pipe,
  prop,
  propEq,
  uniq,
} from "ramda";
import queryString from "query-string";
import { checkForError, getJson, formUrlEncode } from "fetchUtils";

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
const OAUTH_CLIENT_ACCESS_KEY = process.env.REACT_APP_OAUTH_CLIENT_ACCESS_KEY;
const BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY = "biblioboard_token"; // shared across biblioboard subdomains

const OAUTH_AUTHORIZATION_BASE_URL = `${AUTH_BASE_URL}/oauth/authorize?response_type=code&client_id=${OAUTH_CLIENT_ACCESS_KEY}&redirect_uri=`;
const OAUTH_TOKEN_URL = `${AUTH_BASE_URL}/oauth/token`;
const SESSION_PRINCIPAL_URL = `${AUTH_BASE_URL}/authentication/subject`;
const ORGANIZATION_PROFILE_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/organizations`;
const CHANGE_PASSWORD_BASE_URL = `${AUTH_BASE_URL}/oauth/password-change`;

const getAppRedirectUrl = redirectPath =>
  `${window.location.origin}${redirectPath}`;

const getOAuthAuthorizationUrl = redirectPath =>
  `${OAUTH_AUTHORIZATION_BASE_URL}${encodeURIComponent(
    getAppRedirectUrl(redirectPath)
  )}`;

const getOrganizationProfileUrl = organizationId =>
  `${ORGANIZATION_PROFILE_BASE_URL}/${organizationId}`;

const getBuyingGroupMemberUrl = organizationId =>
  `${AUTH_BASE_URL}/buying_groups/${organizationId}/members`;

const getChangePasswordUrl = token =>
  `${CHANGE_PASSWORD_BASE_URL}?redirectUri=${getAppRedirectUrl(
    ""
  )}&token=${token}`;

export const setBiblioboardDomainCookie = token => {
  const regex = new RegExp("test");
  const localhost = new RegExp("localhost");
  const isLocalhost = localhost.test(window.location.origin);
  if (!regex.test(window.location.origin)) {
    if (token) {
      let cookie = `${BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY}=${token}; max-age=3600; path=/`;
      if (isLocalhost) {
        document.cookie = cookie;
      } else {
        document.cookie = cookie + "; domain=.biblioboard.com";
      }
    } else {
      expireBiblioboardDomainToken();
    }
  }
};

export const getBiblioboardDomainToken = () => {
  const regex = new RegExp("test");
  if (regex.test(window.location.origin)) {
    return undefined;
  } else {
    const token = document.cookie
      .split(";")
      .find(row => row.trim().startsWith(BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY))
      ?.split("=")[1];
    return token;
  }
};

export const expireBiblioboardDomainToken = () => {
  let cookie = `${BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
  const localhost = new RegExp("localhost");
  const isLocalhost = localhost.test(window.location.origin);

  if (isLocalhost) {
    document.cookie = cookie;
  } else {
    document.cookie = cookie + "; domain=.biblioboard.com";
  }
};

const signIn = () => {
  const oauthCode = prop("code", queryString.parse(window.location.search));

  if (isNil(oauthCode)) {
    const domainToken = getBiblioboardDomainToken();
    if (!domainToken) {
      const redirectUrl = getOAuthAuthorizationUrl("");
      console.log("Redirecting to: %O", redirectUrl);
      window.location.href = redirectUrl;
    }
    return Promise.resolve();
  } else {
    // clear code from url
    window.history.replaceState(null, null, window.location.pathname);
    return fetchToken(oauthCode);
  }
};

const DIRECT_REPORT_ROLES = ["ROLE_ADMIN", "ROLE_REPORT"];
const INDIRECT_REPORT_ROLES = ["ROLE_REPORT_INHERITED"];
const matchList = (a, b) => intersection(a, b).length > 0; // this function should exist in ramda...
const uniqMerge = (a, b) => pipe(concat, uniq)(a, b);

const reportOrganizationFilter = userRoles => o => {
  const roles = uniqMerge(userRoles, o.roles);
  return (
    matchList(DIRECT_REPORT_ROLES, roles) ||
    matchList(INDIRECT_REPORT_ROLES, roles) // should these be included by default?
  );
};

const indirectOrganizationFilter = userRoles => o =>
  matchList(INDIRECT_REPORT_ROLES, uniqMerge(userRoles, o.roles)) &&
  o.roles.includes("ROLE_BUYING_GROUP");

const mapToken = o => o.access_token;
const mapAuthResult = ({ sessionToken, principals }) => {
  const userPrincipal = find(propEq("type", "USER"), principals);
  const orgPrincipals = filter(propEq("type", "ORGANIZATION"), principals);
  const userRoles = userPrincipal.roles;

  const reportOrgPrincipals = pipe(
    filter(reportOrganizationFilter(userRoles)),
    map(prop("name"))
  )(orgPrincipals);

  const reportBuyingGroups = pipe(
    filter(indirectOrganizationFilter(userRoles)),
    map(prop("name"))
  )(orgPrincipals);

  return Promise.all(map(fetchBuyingGroupMembers, reportBuyingGroups))
    .then(flatten)
    .then(map(prop("organizationId")))
    .then(uniq)
    .then(members =>
      fetchProfiles(
        sessionToken,
        userPrincipal,
        uniq(concat(reportOrgPrincipals, members)),
        reportBuyingGroups
      )
    );
};

const mapAddIsBuyingGroup = reportBuyingGroups => organization => {
  organization.isBuyingGroup = reportBuyingGroups.includes(organization.id);
  // maybe not the right place for this, but works for now...
  if (organization.isBuyingGroup) {
    organization.name = `${organization.name} (Consortium)`;
  }
  return organization;
};

const fetchProfiles = (
  sessionToken,
  userPrincipal,
  principalIds,
  reportBuyingGroups
) => {
  const orgProfileFn = fetchOrganizationProfile(sessionToken);
  const addIsBuyingGroup = mapAddIsBuyingGroup(reportBuyingGroups);

  return Promise.all(map(orgProfileFn, principalIds))
    .then(organizations => map(addIsBuyingGroup, organizations))
    .then(organizations => ({
      token: sessionToken,
      user: {
        id: userPrincipal.name,
        username: userPrincipal.alias,
        roles: userPrincipal.roles,
        organizations,
      },
    }));
};

const fetchToken = code =>
  fetch(OAUTH_TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
    body: formUrlEncode({
      client_id: OAUTH_CLIENT_ACCESS_KEY,
      code,
      grant_type: "authorization_code",
      redirect_uri: getAppRedirectUrl(""),
    }),
  })
    .then(checkForError)
    .then(getJson)
    .then(mapToken);

const fetchUser = token =>
  fetch(SESSION_PRINCIPAL_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      token,
    }),
  })
    .then(checkForError)
    .then(getJson)
    .then(mapAuthResult);

const fetchOrganizationProfile = curry((token, orgId) =>
  fetch(getOrganizationProfileUrl(orgId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  })
    .then(checkForError)
    .then(getJson)
);

const fetchBuyingGroupMembers = organizationId =>
  fetch(getBuyingGroupMemberUrl(organizationId), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  })
    .then(checkForError)
    .then(getJson);

const changePassword = token => {
  window.location.href = getChangePasswordUrl(token);
};

export default {
  signIn,
  fetchUser,
  changePassword,
  getBiblioboardDomainToken,
  setBiblioboardDomainCookie,
};
